/**
 * Workspace Compatibility Resolver – Phase 1 Infrastructure.
 *
 * Resolves which workspace (if any) the current user should see.
 *
 * Resolution priority (mirrors backend):
 *   1. Direct user assignment
 *   2. Role assignment
 *   3. Tenant default workspace
 *   4. Fallback → existing legacy launcher system (no change)
 *
 * IMPORTANT: This module is ADDITIVE. The existing launcher/homepage system
 * is never modified. When `useLegacy` is true, callers MUST fall through to
 * the standard launcher behavior unchanged.
 *
 * Usage:
 *   import { resolveWorkspace } from '@/services/workspace/workspaceResolver';
 *   const result = await resolveWorkspace();
 *   if (result.useLegacy) {
 *     // continue with existing HomeLauncherPage / launcher behavior
 *   } else {
 *     // render result.workspace
 *   }
 */
import { resolveCurrentWorkspace } from '../../api/workspaceApi';

/**
 * @typedef {Object} WorkspaceResolution
 * @property {boolean} useLegacy     – true → use existing launcher, false → use workspace
 * @property {string}  source        – 'workspace' | 'legacy-launcher'
 * @property {Object|null} workspace – workspace object if source === 'workspace', else null
 */

/**
 * Resolve the workspace for the currently authenticated user.
 *
 * Always resolves (never rejects): any error silently falls back to legacy.
 *
 * @returns {Promise<WorkspaceResolution>}
 */
export async function resolveWorkspace() {
  try {
    const response = await resolveCurrentWorkspace();
    const data = response?.data ?? response;

    if (!data || data.fallback || data.source === 'legacy-launcher') {
      return { useLegacy: true, source: 'legacy-launcher', status: 'legacy', workspace: null };
    }

    if (data.source === 'workspace' && data.workspace) {
      return { useLegacy: false, source: 'workspace', status: 'workspace', workspace: data.workspace };
    }

    return { useLegacy: true, source: 'legacy-launcher', status: 'legacy', workspace: null };
  } catch (err) {
    if (import.meta.env?.DEV) {
      console.warn('[WorkspaceResolver] resolve failed:', err);
    }
    return { useLegacy: false, source: 'unavailable', status: 'unavailable', workspace: null };
  }
}

/**
 * Convert a legacy launcher layout object into a workspace version payload.
 * This is a CLIENT-SIDE utility for future migration tooling.
 * It does NOT alter any existing Firestore data or launcher behavior.
 *
 * @param {string} tenantId
 * @param {string} workspaceId
 * @param {Object} layoutData  – existing launcher layout (icons, gridColumns, …)
 * @param {string} createdBy
 * @returns {Object}           – workspaceVersion payload (not saved anywhere)
 */
export function buildVersionFromLauncherLayout(tenantId, workspaceId, layoutData, createdBy = 'system') {
  return {
    workspaceId,
    tenantId,
    versionNumber: 1,
    state: 'draft',
    layoutSchema: {
      icons: layoutData?.icons ?? [],
      gridColumns: layoutData?.gridColumns ?? 4,
      gridRows: layoutData?.gridRows ?? 6,
      iconSize: layoutData?.iconSize ?? 'medium',
      gap: layoutData?.gap ?? 2,
      showGrid: layoutData?.showGrid ?? false,
      theme: layoutData?.theme ?? 'system',
    },
    metadata: { source: 'legacy-launcher' },
    createdBy,
  };
}
